import { useTranslation } from 'react-i18next';
import { SharedDisclosureProps } from '../types';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalOverlay,
  Spinner,
  FormControl,
} from '@chakra-ui/react';
import { samplesApi } from '@texas/api/endpoints/samplesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useEffect, useState } from 'react';
import { request } from '@texas/utils/helpers/httpHelpers';
import { SamplesTable } from './SamplesTable';
import { ErrorDetails } from '../alert/ErrorDetails';
import { User, userApi } from '@texas/api/endpoints/userApi';
import { TexasFormLabel } from '../form/TexasFormLabel';
import { TexasSelect } from '../form/TexasSelect';

interface Props extends SharedDisclosureProps {
  sampleIds: number[];
  onPerformed: () => void;
}

export function UpdateOwner(props: Props) {
  const { t } = useTranslation();

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('sample.changeOwner')}</ModalHeader>
        <ModalCloseButton />
        <Inner
          sampleIds={props.sampleIds}
          onPerform={() => {
            props.onPerformed();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  sampleIds,
  onPerform,
}: {
  sampleIds: number[];
  onPerform: () => void;
}) {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);

  const {
    data: samples,
    refetch: refetchSamples,
    loading: samplesLoading,
    error,
  } = useApiResource(samplesApi.fromIds);

  const {
    data: users,
    refetch: refetchUsers,
    loading: usersLoading,
    error: usersError,
  } = useApiResource(userApi.getUsers);

  const {
    request: updateRequest,
    loading: updateLoading,
    error: updateError,
  } = useApiRequest(samplesApi.updateOwner);

  useEffect(() => {
    refetchSamples(sampleIds);
  }, [refetchSamples, sampleIds]);

  useEffect(() => {
    refetchUsers();
  }, [refetchUsers]);

  const req = () => {
    if (!user) return;
    request(updateRequest, [sampleIds, user.id], () => onPerform());
  };

  return (
    <>
      <ModalBody>
        <Flex flexDir="column" gap={2} w="full">
          {error && <ErrorDetails error={error} />}
          {updateError && <ErrorDetails error={updateError} />}
          {samplesLoading && <Spinner />}
          <SamplesTable samples={samples ?? []} />
          <FormControl>
            <TexasFormLabel>{t('customerUser.select')}</TexasFormLabel>
            <TexasSelect
              isLoading={usersLoading}
              onChange={(e) => {
                setUser(users?.find((u) => u.id === e?.value) ?? null);
              }}
              options={users?.map((u) => ({
                label: `${u.name}${u.email ? ` (${u.email})` : ''}`,
                value: u.id,
              }))}
            />
            <ErrorDetails error={usersError} />
          </FormControl>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          isLoading={updateLoading}
          disabled={updateLoading || user === null}
          onClick={() => req()}
        >
          {t('general.save')}
        </Button>
      </ModalFooter>
    </>
  );
}
